import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { DepartureIcon, ListApprovedIcon, PinIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { attendAlone } from '../../../../api/affected-staffs/attend-alone'
import { getAffectedStaff } from '../../../../api/affected-staffs/get-affected-staff'
import { getAffectedStaffs } from '../../../../api/affected-staffs/get-affected-staffs'
import { getForeignStaffTravelSummary } from '../../../../api/affected-staffs/get-foreign-staff-travel-summary'
import { joinGroup } from '../../../../api/affected-staffs/join-group'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { TravelChoiceEnum } from '../../../../lib/types/enums/affected-staff/travel-choice.enum'
import {
  TransportTypeEnum,
  transportTypeMapping
} from '../../../../lib/types/enums/trip/trip-type.enum'
import { TripStepTypeEnum } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { AffectedStaffMap } from '../../../../lib/types/models/affectedStaff/affected-staff'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTravelScheduleTripTypeOption } from '../../../../lib/utils/functions/travel-schedule.functions'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import { ItemNotFound } from '../../../elements/item-not-found'
import LoadingScreen from '../../../elements/loading/loading-screen'
import TransportPicto from '../../../elements/transport-picto'
import { LabelValue } from '../../language-test/client-space/shared-client-space-ui'
import { convertTimeZone } from '../../trip-folders/trip-folder-reservation/trip-folder-reservation-details'
import { TravelScheduleProps } from './travel-schedule.types'

export function ForeignStaffTravelSchedulePage(props: TravelScheduleProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data, isPending } = useDataProps<AffectedStaffMap['FULL'], TravelScheduleProps>(
    props,
    (id) => getAffectedStaff(id, 'FULL')
  )
  const center = data?.staffRequest.centers.at(0)

  // The layout owns the affected staff query and feeds the context; invalidating it
  // refreshes every consumer instead of keeping a second copy of the same resource here.
  const refetchAffectedStaff = () =>
    queryClient.invalidateQueries({ queryKey: ['affected-staff'] })

  const { data: travelSummary, isPending: isTravelSummaryPending } = useQuery({
    queryKey: ['affected-staff', data?.id, 'foreign-travel-summary'],
    queryFn: () => {
      if (!data?.id) return
      return getForeignStaffTravelSummary(data.id)
    },
    enabled: !!data?.id
  })

  // Meeting place = St Pancras, only sent for centers with a Eurostar journey (else no group option)
  const inbound = travelSummary?.group.inbound
  const outbound = travelSummary?.group.outbound
  const centerManager = travelSummary?.individual?.centerManager ?? undefined
  const meetingPlace = travelSummary?.group.meetingPlaceLabel ?? undefined
  const canJoinGroup = Boolean(travelSummary?.group.canJoinGroup)

  const { data: contactAffectedStaffs } = useQuery({
    queryKey: ['affected-staffs', `center-id-${center?.id}`],
    queryFn: () => getAffectedStaffs({ filter: { centerId: center?.id } }, 'BASIC')
  })

  const joinGroupMutation = useMutation({
    mutationFn: joinGroup,
    onError: showAxiosError,
    onSuccess: async () => await refetchAffectedStaff()
  })

  const attendAloneMutation = useMutation({
    mutationFn: attendAlone,
    onError: showAxiosError,
    onSuccess: async () => await refetchAffectedStaff()
  })

  const tripLabel = TripProperties.getTripLabel(inbound?.trip ?? undefined)
  const tripPicto = inbound?.transportMeans?.picto

  const staffContacts = contactAffectedStaffs?.data
    ?.filter(({ staff }) => staff.id !== data?.staff.id)
    // filter out duplicate staffs
    .filter(({ staff }, index, self) => index === self.findIndex((t) => t.staff.id === staff.id))

  const inboundTripTypeOption = getTravelScheduleTripTypeOption(
    TripStepTypeEnum.INBOUND,
    !!outbound
  )
  // the individual travel always shows the return block next to the departure
  const individualInboundTripTypeOption = getTravelScheduleTripTypeOption(
    TripStepTypeEnum.INBOUND,
    true
  )

  if (isPending) return <LoadingScreen />
  if (!data) return <ItemNotFound title="Aucun staff trouvé" />
  if (!data.travelChoiceDiffused)
    return (
      <div className="space-y-4">
        <Typography variant="h3">
          {t('staff.personal.space.travel.schedule.my.meeting.points')}
        </Typography>

        <Box style="border">
          <Typography variant="secondary-body">
            {t('staff.personal.space.travel.schedule.schedules.not.available.yet')}
          </Typography>
        </Box>
      </div>
    )
  // The choices depend on the summary (joining the group is Eurostar only): wait for it
  if (isTravelSummaryPending) return <LoadingScreen />
  return (
    <div className="space-y-4">
      <Typography variant="h3">
        {t('staff.personal.space.travel.schedule.my.meeting.points')}
      </Typography>

      <Box style="shadow" className="bg-primary-light shadow-[0px_6px_12px_0px_#0036B430]">
        <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
          {canJoinGroup
            ? t('staff.personal.space.travel.schedule.foreign.leader.options.title', {
                meetingPlace: valueRender(meetingPlace)
              })
            : !data?.travelChoice &&
              t('staff.personal.space.travel.schedule.foreign.leader.options.title.attend.alone')}
        </Typography>

        {!data?.travelChoice ? (
          travelSummary && (
            <div className="flex gap-2 mobile:flex-col tab:flex-col">
              {canJoinGroup && (
                <Button
                  label={t(
                    'staff.personal.space.travel.schedule.foreign.leader.options.join.group',
                    {
                      meetingPlace: valueRender(meetingPlace)
                    }
                  )}
                  loading={joinGroupMutation.isPending}
                  onClick={() => joinGroupMutation.mutateAsync(data.id)}
                />
              )}
              <Button
                label={t(
                  'staff.personal.space.travel.schedule.foreign.leader.options.attend.alone'
                )}
                loading={attendAloneMutation.isPending}
                onClick={() => attendAloneMutation.mutateAsync(data.id)}
              />
            </div>
          )
        ) : (
          <Typography
            variant="caption"
            color={tailwindColors.blue.DEFAULT}
            className='before:content-[""] before:mr-2 before:inline-block before:w-2 before:h-2 before:rounded-full before:bg-primary-light-active'
          >
            {data?.travelChoice === TravelChoiceEnum.GROUP
              ? meetingPlace
                ? t('staff.personal.space.travel.schedule.foreign.leader.options.join.group', {
                    meetingPlace
                  })
                : t(
                    'staff.personal.space.travel.schedule.foreign.leader.options.join.group.generic'
                  )
              : t('staff.personal.space.travel.schedule.foreign.leader.options.attend.alone')}
          </Typography>
        )}
      </Box>

      {/* Static text */}
      {!data?.travelChoice && (
        <Box style="border" className="bg-white-hover">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded flex items-center justify-center">
              <PinIcon className="w-8 h-8" />
            </div>
            <Typography variant="tag" color={tailwindColors.grey.DEFAULT}>
              {t(
                'staff.personal.space.travel.schedule.reminder.about.the.expense.reimbursement.policy'
              )}
            </Typography>
          </div>
        </Box>
      )}

      {data?.travelChoice === TravelChoiceEnum.GROUP && (
        <Box style="border">
          <div className="flex items-center gap-2">
            {tripPicto && <TransportPicto picto={tripPicto} className="bg-primary-light" />}
            <Typography variant="sub-heading" color={tailwindColors.primary.DEFAULT}>
              {tripLabel}
            </Typography>
          </div>

          <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1 tab:grid-cols-1">
            {inbound && (
              <Box style="border" className={'bg-white-hover'}>
                <Badge
                  text={`Voyage ${inboundTripTypeOption.label.toLowerCase()}`}
                  variant={inboundTripTypeOption.variant}
                />

                <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.options.join.group.departure.label'
                  )}
                </Typography>

                <Box className="grid mobile:grid-cols-1 tab:grid-cols-1 grid-cols-2 gap-3 bg-primary-light rounded p-3">
                  <LabelValue
                    label={t(
                      'staff.personal.space.travel.schedule.foreign.leader.options.join.group.rdv.hour'
                    )}
                    color={tailwindColors.grey.lighter}
                  >
                    <Badge
                      variant={'secondaryRed'}
                      text={valueRender(inbound.meetingTime ?? undefined, (time) =>
                        convertTimeZone(
                          time,
                          'Local',
                          inbound.meetingTimeZone ?? undefined
                        ).replace(':', 'h')
                      )}
                    />
                  </LabelValue>

                  <LabelValue
                    label={t(
                      'staff.personal.space.travel.schedule.foreign.leader.options.join.group.rdv.place'
                    )}
                    color={tailwindColors.grey.lighter}
                  >
                    <Typography variant="tag">{inbound.meetingPlace}</Typography>
                  </LabelValue>
                </Box>

                {staffContacts && staffContacts.length > 0 && (
                  <LabelValue
                    label={t(
                      'staff.personal.space.travel.schedule.foreign.leader.options.join.group.contact.label'
                    )}
                    color={tailwindColors.grey.lighter}
                  >
                    <div className="flex flex-wrap gap-2">
                      {staffContacts?.map(({ staff }) => (
                        <Badge
                          key={staff.id}
                          size="xsmall"
                          text={`${toFullName(staff.firstName, staff.lastName)} - ${formatPhoneNumber(staff.phoneNumber)}`}
                        />
                      ))}
                    </div>
                  </LabelValue>
                )}

                <Box style="border" className="bg-white-hover p-2 rounded">
                  <div className="flex items-start gap-2">
                    <div className="size-6 rounded flex items-center justify-center">
                      <PinIcon className="size-6" />
                    </div>
                    <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                      {t(
                        'staff.personal.space.travel.schedule.foreign.leader.options.join.group.note'
                      )}
                    </Typography>
                  </div>
                </Box>
              </Box>
            )}

            {outbound && (
              <Box style="border" className={'bg-white-hover h-fit'}>
                <Badge
                  text={`Voyage ${transportTypeMapping[TransportTypeEnum.RETURN].label.toLowerCase()}`}
                  variant={transportTypeMapping[TransportTypeEnum.RETURN].variant}
                />

                <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.options.join.group.bus.text'
                  )}
                </Typography>

                <Box className="grid mobile:grid-cols-1 tab:grid-cols-1 grid-cols-2 gap-3 bg-primary-light rounded p-3">
                  <LabelValue
                    label={t(
                      'staff.personal.space.travel.schedule.foreign.leader.options.join.group.dropoff.hour'
                    )}
                    color={tailwindColors.grey.lighter}
                  >
                    <Badge
                      variant={'secondaryRed'}
                      text={valueRender(outbound.arrivalTime ?? undefined, (time) =>
                        convertTimeZone(
                          time,
                          'Local',
                          outbound.arrivalTimeZone ?? undefined
                        ).replace(':', 'h')
                      )}
                    />
                  </LabelValue>

                  <LabelValue
                    label={t(
                      'staff.personal.space.travel.schedule.foreign.leader.options.join.group.dropoff.place'
                    )}
                    color={tailwindColors.grey.lighter}
                  >
                    <Typography variant="tag">{outbound.arrivalPlace}</Typography>
                  </LabelValue>
                </Box>
              </Box>
            )}
          </div>
        </Box>
      )}

      {data?.travelChoice === TravelChoiceEnum.INDIVIDUAL && (
        <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1 tab:grid-cols-1">
          <Box style="border" className={'bg-white-hover'}>
            <Badge
              text={`Voyage ${individualInboundTripTypeOption.label.toLowerCase()}`}
              variant={individualInboundTripTypeOption.variant}
            />

            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              {t(
                'staff.personal.space.travel.schedule.foreign.leader.attend.alone.departure.text'
              )}
            </Typography>

            <Box className="grid mobile:grid-cols-1 tab:grid-cols-1 grid-cols-2 gap-2 bg-primary-light rounded p-3">
              <LabelValue
                label={t(
                  'staff.personal.space.travel.schedule.foreign.leader.attend.alone.address.label'
                )}
                color={tailwindColors.grey.lighter}
              >
                <Typography variant="label-semibold">
                  {valueRender(data.staffRequest.centers.at(0)?.journeyPlace?.address)}
                </Typography>
              </LabelValue>

              <LabelValue
                label={t(
                  'staff.personal.space.travel.schedule.foreign.leader.attend.alone.time.label'
                )}
                color={tailwindColors.grey.lighter}
              >
                {/* this is static always 16:00 */}
                <Badge variant={'secondaryRed'} text={'16:00'} />
              </LabelValue>
            </Box>

            <LabelValue
              label={t(
                'staff.personal.space.travel.schedule.foreign.leader.attend.alone.responsible.label'
              )}
              color={tailwindColors.grey.lighter}
            >
              <Badge
                text={valueRender(
                  centerManager,
                  ({ firstName, lastName, phoneNumber }) =>
                    `${StringFormatter.capitalize(firstName)} ${lastName.toUpperCase()}${phoneNumber ? ` - ${formatPhoneNumber(phoneNumber)} (Numéro Etranger)` : ''}`
                )}
              />
            </LabelValue>

            <Box style="border" className="bg-white-hover p-2 rounded">
              <div className="flex items-center gap-2">
                <div className="size-6 rounded flex items-center justify-center">
                  <PinIcon className="size-6" />
                </div>
                <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
                  {t('staff.personal.space.travel.schedule.foreign.leader.attend.alone.note')}
                </Typography>
              </div>
            </Box>
          </Box>

          <Box style="border" className={'bg-white-hover h-fit'}>
            <Badge
              text={`Voyage ${transportTypeMapping[TransportTypeEnum.RETURN].label.toLowerCase()}`}
              variant={transportTypeMapping[TransportTypeEnum.RETURN].variant}
            />

            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              {t('staff.personal.space.travel.schedule.foreign.leader.attend.alone.return.text')}
            </Typography>
          </Box>
        </div>
      )}

      {/* Checklist of things to prepare on the day of departure */}
      <Box style="border">
        <div className="flex items-center gap-2">
          <ListApprovedIcon
            color={tailwindColors.primary.DEFAULT}
            className="bg-primary-light rounded p-1 size-8"
          />
          <Typography variant="sub-heading" className="text-blue-dark-hover">
            {t('staff.personal.space.travel.schedule.checklist.title')}
          </Typography>
        </div>

        <div className="bg-red-light p-6 rounded-3xl mobile:p-2 tab:p-2 relative">
          <div className="w-full m-auto max-w-[800px] absolute z-0 left-1/2 -translate-x-1/2">
            <img src="/waiting-passengers.png" alt="waiting-passengers" />
          </div>

          <div className="h-[330px] mobile:h-[150px]"></div>

          <Box style="shadow" className="rounded-2xl space-y-2 z-20 relative">
            <Box
              style="border"
              className="border-red-light flex flex-row gap-2 p-2 items-center rounded-lg"
            >
              <DepartureIcon
                color={tailwindColors.red.DEFAULT}
                className="bg-red-light rounded p-1 min-w-8 size-8"
              />

              <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
                {t('staff.personal.space.travel.schedule.checklist.subtitle')}
              </Typography>
            </Box>

            <div className="space-y-3">
              <div>
                <Typography variant="sub-heading" className="inline">
                  🔹{' '}
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.checklist.item.one.title'
                  )}{' '}
                  :{' '}
                </Typography>

                <Typography variant="sub-heading" className="inline !font-light">
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.checklist.item.one.details'
                  )}
                </Typography>
              </div>

              <div>
                <Typography variant="sub-heading" className="inline !font-light">
                  🔹{' '}
                  {t('staff.personal.space.travel.schedule.foreign.leader.checklist.item.two.one')}{' '}
                  :{' '}
                </Typography>

                <Typography variant="sub-heading" className="inline">
                  {t('staff.personal.space.travel.schedule.foreign.leader.checklist.item.two.two')}
                </Typography>

                <Typography variant="sub-heading" className="inline !font-light mx-1">
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.checklist.item.two.three'
                  )}
                </Typography>

                <Typography variant="sub-heading" className="inline">
                  {t(
                    'staff.personal.space.travel.schedule.foreign.leader.checklist.item.two.four'
                  )}
                </Typography>
              </div>
            </div>
          </Box>
        </div>
      </Box>
    </div>
  )
}
